import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'

import { Icon as Iconify } from '@iconify/react'
import { appColors } from 'utils'
import DataConfigFormItem from 'components/WellList/DataConfigModal/DataConfigFormItem'
import useInnovaTheme from 'components/common/hooks/useInnovaTheme'
import { GlassTitle, useEngineeringUi } from 'components/WellPages/Engineering/Utils/EngineeringStyling'
import { unescapeHtml } from 'utils/htmlSymbolHandling'

const emptyUser = {
  emailFrom: '',
  emailReply: '',
  actions: [],
  surveyUnits: '',
  mudUnits: '',
  dateFormat: '',
}

const uniq = (arr) => {
  const seen = new Set()
  const values = []

  for (const value of arr || []) {
    const formattedValue = String(value ?? '').trim()

    if (!formattedValue) continue
    if (seen.has(formattedValue)) continue

    seen.add(formattedValue)
    values.push(formattedValue)
  }

  return values
}

const isValidEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

const DataBridgeUserModal = ({ open, action, user, dropDowns, onSave, onClose }) => {
  const { theme, getTextColor, getDisabledColor, getCardBackground, getBackColor, getWindowBarColor } = useInnovaTheme()

  const ui = useEngineeringUi(true)
  const isEdit = action === 'EDIT'

  const [formData, setFormData] = useState({ ...emptyUser })
  const [showErrors, setShowErrors] = useState(false)

  const textColor = getTextColor()
  const cardBackground = getCardBackground()

  const actionDropDowns = useMemo(() => {
    return Array.isArray(dropDowns?.rules) ? dropDowns.rules : []
  }, [dropDowns])

  const surveyUnitDropDowns = useMemo(() => {
    return Array.isArray(dropDowns?.depthUnits) ? dropDowns.depthUnits : []
  }, [dropDowns])

  const mudUnitDropDowns = useMemo(() => {
    return Array.isArray(dropDowns?.mudUnits) ? dropDowns.mudUnits : []
  }, [dropDowns])

  const dateFormatDropDowns = useMemo(() => {
    return Array.isArray(dropDowns?.dateFormat) ? dropDowns.dateFormat : []
  }, [dropDowns])

  useEffect(() => {
    if (!open) return

    if (isEdit && user) {
      setFormData({
        emailFrom: user.emailFrom || '',
        emailReply: user.emailReply || '',
        actions: Array.isArray(user.actions) ? [...user.actions] : [],
        surveyUnits: user.surveyUnits || '',
        mudUnits: user.mudUnits || '',
        dateFormat: user.dateFormat || '',
      })
    } else {
      setFormData({ ...emptyUser })
    }

    setShowErrors(false)
  }, [open, isEdit, user])

  const inputSx = useMemo(
    () => ({
      ...ui.input,
      width: '100%',
      '& .MuiInputBase-root': {
        minHeight: 40,
        backgroundColor: getBackColor(),
        color: getTextColor(),
      },
      '& .MuiInputBase-input': {
        fontSize: '0.95rem',
        color: getTextColor(),
      },
      '& .MuiInputLabel-root': {
        color: getDisabledColor(),
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme === 'dark' ? '#2f3a45' : '#d0d7de',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#429CEB',
      },
      '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#429CEB',
        boxShadow: '0 0 6px rgba(66,156,235,0.3)',
      },
    }),
    [ui.input, getBackColor, getTextColor, getDisabledColor, theme],
  )

  const autoCompleteSx = useMemo(
    () => ({
      width: '100%',
      '& .MuiOutlinedInput-root': {
        minHeight: 40,
        minWidth: 400,
        backgroundColor: getBackColor(),
        color: getTextColor(),
      },
      '& .MuiInputBase-input': {
        fontSize: '0.95rem',
        color: getTextColor(),
      },
      '& .MuiInputLabel-root': {
        color: getDisabledColor(),
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme === 'dark' ? '#2f3a45' : '#d0d7de',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#429CEB',
      },
      '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#429CEB',
        boxShadow: '0 0 6px rgba(66,156,235,0.3)',
      },
      '& .MuiChip-root': {
        backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
        color: getTextColor(),
      },
      '& .MuiChip-deleteIcon': {
        color: getDisabledColor(),
      },
    }),
    [getBackColor, getTextColor, getDisabledColor, theme],
  )

  const autoCompleteSlotProps = useMemo(
    () => ({
      paper: {
        sx: {
          backgroundColor: cardBackground,
          color: textColor,
          border: theme === 'dark' ? '1px solid rgba(255,255,255,0.10)' : '1px solid rgba(0,0,0,0.10)',
        },
      },
      popper: {
        sx: {
          '& .MuiAutocomplete-listbox': {
            backgroundColor: cardBackground,
            color: textColor,
            padding: 0,
          },
          '& .MuiAutocomplete-option': {
            color: textColor,
          },
          '& .MuiAutocomplete-option[aria-selected="true"]': {
            backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(0,0,0,0.08)',
          },
          '& .MuiAutocomplete-option.Mui-focused': {
            backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
          },
        },
      },
    }),
    [cardBackground, textColor, theme],
  )

  const updateFormData = (patch) => {
    setFormData((prev) => ({
      ...prev,
      ...patch,
    }))
  }

  const emailFromValid = isValidEmail(formData.emailFrom)
  const emailReplyValid = formData.emailReply === '' || isValidEmail(formData.emailReply)

  const canSubmit = emailFromValid && emailReplyValid && formData.actions.length > 0

  const handleSubmit = () => {
    setShowErrors(true)

    if (!canSubmit) return

    onSave?.({
      emailFrom: formData.emailFrom.trim(),
      emailReply: formData.emailReply.trim(),
      actions: uniq(formData.actions),
      surveyUnits: formData.surveyUnits,
      mudUnits: formData.mudUnits,
      dateFormat: formData.dateFormat,
    })
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='md'
      PaperProps={{
        sx: {
          backgroundColor: getWindowBarColor(),
          width: '850px',
          maxWidth: '95vw',
          maxHeight: '90vh',
        },
      }}>
      <Box
        sx={{
          height: '44px',
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          color: appColors.headerTextColor,
          backgroundColor: getWindowBarColor(),
          borderBottom: '1px solid rgba(255,255,255,0.12)',
        }}>
        <Typography
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontWeight: 600,
            flex: 1,
          }}>
          <Iconify icon={isEdit ? 'mdi:pencil-outline' : 'fluent:add-12-filled'} />

          {isEdit ? 'Edit Data Bridge User' : 'Add Data Bridge User'}
        </Typography>

        <IconButton onClick={onClose} size='small'>
          <Iconify icon='mdi:close' />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          p: 1.5,
          backgroundColor: getBackColor(),
        }}>
        <Box
          sx={{
            ...ui.surface,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            width: '100%',
            minWidth: 0,
          }}>
          <GlassTitle title='User Details' ui={ui} />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(2, minmax(0, 1fr))',
              },
              gap: 1,
              width: '100%',
            }}>
            <DataConfigFormItem item={{ label: 'Email' }} >
              <TextField
                fullWidth
                size='small'
                value={formData.emailFrom}
                error={showErrors && !emailFromValid}
                helperText={showErrors && !emailFromValid ? 'A valid email is required' : ''}
                onChange={(event) => {
                  updateFormData({
                    emailFrom: event.target.value,
                  })
                }}
                sx={inputSx}
              />
            </DataConfigFormItem>

            <DataConfigFormItem item={{ label: 'Reply To' }}>
              <TextField
                fullWidth
                size='small'
                value={formData.emailReply}
                error={showErrors && !emailReplyValid}
                helperText={showErrors && !emailReplyValid ? 'Enter a valid email' : ''}
                onChange={(event) => {
                  updateFormData({
                    emailReply: event.target.value,
                  })
                }}
                sx={inputSx}
              />
            </DataConfigFormItem>

            <DataConfigFormItem item={{ label: 'Depth Units' }} labelWidth={115} controlMaxWidth={340}>
              <TextField
                select
                fullWidth
                size='small'
                value={formData.surveyUnits}
                onChange={(event) => {
                  updateFormData({
                    surveyUnits: unescapeHtml(event.target.value ?? ''),
                  })
                }}
                sx={inputSx}>
                <MenuItem value=''>
                  <em>None</em>
                </MenuItem>

                {surveyUnitDropDowns.map((value) => (
                  <MenuItem key={value} value={value}>
                    {unescapeHtml(value)}
                  </MenuItem>
                ))}
              </TextField>
            </DataConfigFormItem>

            <DataConfigFormItem item={{ label: 'Mud Units' }} labelWidth={115} controlMaxWidth={340}>
              <TextField
                select
                fullWidth
                size='small'
                value={formData.mudUnits}
                onChange={(event) => {
                  updateFormData({
                    mudUnits: unescapeHtml(event.target.value ?? ''),
                  })
                }}
                sx={inputSx}>
                <MenuItem value=''>
                  <em>None</em>
                </MenuItem>

                {mudUnitDropDowns.map((value) => (
                  <MenuItem key={value} value={value}>
                    {unescapeHtml(value)}
                  </MenuItem>
                ))}
              </TextField>
            </DataConfigFormItem>

            <DataConfigFormItem item={{ label: 'Date Format' }} labelWidth={115} controlMaxWidth={340}>
              <TextField
                select
                fullWidth
                size='small'
                value={formData.dateFormat}
                onChange={(event) => {
                  updateFormData({
                    dateFormat: unescapeHtml(event.target.value ?? ''),
                  })
                }}
                sx={inputSx}>
                <MenuItem value=''>
                  <em>None</em>
                </MenuItem>

                {dateFormatDropDowns.map((value) => (
                  <MenuItem key={value} value={value}>
                    {unescapeHtml(value)}
                  </MenuItem>
                ))}
              </TextField>
            </DataConfigFormItem>
          </Box>

          <Box
            sx={{
              width: '100%',
              borderBottom: `1px solid ${ui.titleDividerTrack.background}`,
              my: 0.5,
            }}
          />

          <GlassTitle title='Allowed Actions' ui={ui} />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr',
              gap: 1,
              width: '100%',
            }}>
            <DataConfigFormItem item={{ label: 'Actions' }} labelWidth={115} controlMaxWidth={690}>
              <Autocomplete
                multiple
                disableCloseOnSelect
                size='small'
                options={actionDropDowns}
                value={formData.actions}
                onChange={(_, nextValues) => {
                  updateFormData({
                    actions: uniq(nextValues),
                  })
                }}
                sx={autoCompleteSx}
                slotProps={autoCompleteSlotProps}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={showErrors && formData.actions.length === 0}
                    helperText={showErrors && formData.actions.length === 0 ? 'At least one action is required' : ''}
                  />
                )}
              />
            </DataConfigFormItem>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 1.5,
          py: 1,
          backgroundColor: getWindowBarColor(),
          borderTop: '1px solid rgba(255,255,255,0.12)',
        }}>
        <Button variant='outlined' color='secondary' onClick={onClose}>
          Cancel
        </Button>

        <Button variant='outlined' color='primary' onClick={handleSubmit}>
          {isEdit ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default DataBridgeUserModal
